import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';

import clientsReducer from '@/store/clientsSlice';

import authReducer from './slices/authSlice';
import organizationReducer from './slices/organizationSlice';
import themeReducer from './slices/themeSlice';
import featureToggleReducer from '../slices/featureToggleSlice';
import notificationReducer from '../slices/notificationSlice';
import analyticsReducer from './slices/analyticsSlice';
import caseReducer from './slices/caseSlice';
import dashboardReducer from './slices/dashboardSlice';
import domainReducer from './slices/domainSlice';

const rootReducer = combineReducers({
  theme: themeReducer,
  auth: authReducer,
  organization: organizationReducer,
  featureToggle: featureToggleReducer,
  notification: notificationReducer,
  cases: caseReducer,
  dashboard: dashboardReducer,
  analytics: analyticsReducer,
  domain: combineReducers({
    // ✅ canonical clients mount under domain.clients
    clients: clientsReducer,
    // Preserve existing domain state under a distinct key used across the app
    snapshot: domainReducer as any,
  }) as any,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  // @ts-ignore
  window.__store = store;
}

// Development diagnostics for mounted reducers
if (process.env.NODE_ENV === 'development') {
  try {
    // eslint-disable-next-line no-console
    console.log('[Redux Mounts]', Object.keys(store.getState?.() || {}));
    // eslint-disable-next-line no-console
    console.log('[Domain Mounts]', Object.keys((store.getState?.() as any)?.domain || {}));
  } catch {}
}

// Explicitly define the RootState type
export type RootState = {
  theme: ReturnType<typeof themeReducer>;
  auth: ReturnType<typeof authReducer>;
  organization: ReturnType<typeof organizationReducer>;
  featureToggle: ReturnType<typeof featureToggleReducer>;
  notification: ReturnType<typeof notificationReducer>;
  cases: ReturnType<typeof caseReducer>;
  dashboard: ReturnType<typeof dashboardReducer>;
  analytics: ReturnType<typeof analyticsReducer>;
  domain: {
    clients: ReturnType<typeof clientsReducer>;
    snapshot: ReturnType<typeof domainReducer>;
  };
};

export type AppDispatch = typeof store.dispatch;

// Export default for components that prefer default import
export default store;
